import { useState, useContext, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CardOne } from "../../components/cards/cardOne";
import { GoBackButton } from "../../components/buttons/goBack";
import { themeContext } from "../../context/theme";
import { gameContext } from "../../context/game";
import { useAuth } from "../../context/auth";
import { useBoardSkin } from "../../context/boardSkin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faPlus,
  faSignInAlt,
  faKey,
  faCopy,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./PrivateRoomsPage.module.css";
import { RoomLobby } from "./RoomLobby";
import { useNetworkService } from "../../hooks/useNetworkService";
import { VirtualKeyboardModal } from "../../components/VirtualKeyboard";
import { useGamepad } from "../../hooks/useGamepad";
import { focusOnMouseMove, playSound } from "../../utilities/utilities";

export const PrivateRoomsPage = ({ onBack }) => {
  const { user } = useAuth(); // Get logged-in user
  const [currentView, setCurrentView] = useState("main"); // main, lobby
  const [roomCodeInput, setRoomCodeInput] = useState("");
  const [playerNameInput, setPlayerNameInput] = useState(user?.name || ""); // Use logged-in user's name as default
  const [playerNameError, setPlayerNameError] = useState("");
  const [currentPlayerName, setCurrentPlayerName] = useState("");
  const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(false);
  const [keyboardInputType, setKeyboardInputType] = useState(null); // 'name' or 'code'
  const [hasGamepad, setHasGamepad] = useState(false);
  const [isUIReady, setIsUIReady] = useState(false);
  const [hasUserEditedName, setHasUserEditedName] = useState(false);
  const { theme } = useContext(themeContext);
  const themeNumber = theme.replace("theme-", ""); // Extract number from "theme-0"
  const game = useContext(gameContext);
  const { selectedPremiumBoard } = useBoardSkin(); // Get selected premium board
  const navigate = useNavigate();
  const roomCodeInputRef = useRef(null);
  const playerNameInputRef = useRef(null);
  const isMouseModeRef = useRef(false);
  const lastViewChangeTime = useRef(0);

  // Focusable elements for keyboard/gamepad navigation
  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, [currentView]);

  // Use the networking service
  const {
    isConnected,
    connectionError,
    roomData,
    playerId,
    isLoading,
    connect,
    createRoom,
    joinRoom,
    leaveRoom,
    clearError,
  } = useNetworkService();

  // Set default player name from logged-in user only once on mount
  useEffect(() => {
    if (user?.name && !playerNameInput && !hasUserEditedName) {
      setPlayerNameInput(user.name);
    }
  }, [user]); // Only run when user changes, not when playerNameInput changes

  // Auto-connect to server when component mounts or when returning to main view
  useEffect(() => {
    if (!isConnected && currentView === "main") {
      connect();
    }
  }, [isConnected, connect, currentView]);

  // Cleanup socket connection on unmount (e.g. browser back button, URL change)
  useEffect(() => {
    return () => {
      leaveRoom();
    };
  }, [leaveRoom]);

  // Handle room creation
  const handleCreateRoom = async () => {
    const trimmedName = playerNameInput.trim();
    if (!trimmedName) {
      setPlayerNameError("Player name is required");
      return;
    }
    setPlayerNameError("");

    if (!isConnected) {
      clearError();
      const connected = await connect();
      if (!connected) {
        return;
      }
    }

    try {
      await createRoom(trimmedName, theme, selectedPremiumBoard);
      setCurrentPlayerName(trimmedName);
      setCurrentView("lobby");
      game?.sounds?.clickSound?.play();
    } catch (err) {
      game?.sounds?.clickSound?.play();
    }
  };

  // Handle room joining
  const handleJoinRoom = async () => {
    const trimmedName = playerNameInput.trim();
    if (!trimmedName) {
      setPlayerNameError("Player name is required");
      return;
    }
    setPlayerNameError("");

    if (!roomCodeInput.trim()) {
      return;
    }

    if (roomCodeInput.length !== 6) {
      return;
    }

    if (!isConnected) {
      clearError();
      const connected = await connect();
      if (!connected) {
        return;
      }
    }

    try {
      await joinRoom(
        roomCodeInput.toUpperCase(),
        trimmedName,
        theme,
        selectedPremiumBoard
      );
      setCurrentPlayerName(trimmedName);
      setCurrentView("lobby");
      game?.sounds?.clickSound?.play();
    } catch (err) {
      game?.sounds?.clickSound?.play();
    }
  };

  const handleBackToMain = () => {
    setCurrentView("main");
    setRoomCodeInput("");
    setCurrentPlayerName("");
    clearError();
    leaveRoom();
    lastViewChangeTime.current = Date.now();
    game?.sounds?.clickSound?.play();
  };

  const handleCopyRoomCode = () => {
    if (roomData?.code) {
      navigator.clipboard.writeText(roomData.code);
      game?.sounds?.clickSound?.play();
    }
  };

  const handleRoomCodeInputChange = (e) => {
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 6);
    setRoomCodeInput(value);
    clearError();
  };

  const handleRoomCodeKeyDown = (e) => {
    if (e.key === "Enter" && !isLoading) {
      e.preventDefault();
      e.stopPropagation();
      handleJoinRoom();
    }
  };

  useEffect(() => {
    if (roomCodeInputRef.current && currentView === "main") {
      roomCodeInputRef.current.focus();
    }
  }, [currentView]);

  // Detect gamepad connection
  useEffect(() => {
    const checkGamepad = () => {
      const gamepads = navigator.getGamepads();
      const hasGp = Array.from(gamepads).some(gp => gp !== null);
      setHasGamepad(hasGp);
    };

    checkGamepad();
    window.addEventListener("gamepadconnected", checkGamepad);
    window.addEventListener("gamepaddisconnected", checkGamepad);

    // Check periodically in case events don't fire
    const interval = setInterval(checkGamepad, 2000);

    return () => {
      window.removeEventListener("gamepadconnected", checkGamepad);
      window.removeEventListener("gamepaddisconnected", checkGamepad);
      clearInterval(interval);
    };
  }, []);

  // Wait for page transition/animation to complete before accepting gamepad input
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsUIReady(true);
    }, 500); // Small delay to prevent accidental inputs during page transition

    return () => clearTimeout(timer);
  }, []);

  // Initialize focusable elements
  useEffect(() => {
    if (currentView === 'main' && !showVirtualKeyboard) {
      const allElements = document.querySelectorAll("[tabindex='0']");
      const elements = Array.from(allElements).filter(el => !el.disabled);
      focusableElements.elements = elements;
      if (elements.length > 0) {
        focusableElements.index = 0;
        elements[0].focus();
        isMouseModeRef.current = false;
      }
    }
  }, [currentView, showVirtualKeyboard, focusableElements]);

  // Handle gamepad navigation with circular navigation for online multiplayer
  const handleGamepadNavigation = useCallback((direction) => {
    // Switch to keyboard mode
    isMouseModeRef.current = false;

    // Re-query elements on each navigation to ensure they're up to date
    // Filter out disabled elements
    const allElements = document.querySelectorAll("[tabindex='0']");
    const elements = Array.from(allElements).filter(el => !el.disabled);
    if (elements.length === 0) return;

    focusableElements.elements = elements;

    // Find current focused element's index
    const currentIndex = elements.indexOf(document.activeElement);
    let newIndex;

    if (direction === 'down') {
      if (currentIndex === -1) {
        // Not found, start from beginning
        newIndex = 0;
      } else if (currentIndex >= elements.length - 1) {
        // At end, wrap to first
        newIndex = 0;
      } else {
        // Move to next
        newIndex = currentIndex + 1;
      }
    } else if (direction === 'up') {
      if (currentIndex === -1) {
        // Not found, start from end
        newIndex = elements.length - 1;
      } else if (currentIndex <= 0) {
        // At start, wrap to last
        newIndex = elements.length - 1;
      } else {
        // Move to previous
        newIndex = currentIndex - 1;
      }
    }

    // Update stored index and focus the element
    focusableElements.index = newIndex;
    if (elements[newIndex]) {
      playSound(game, "navigation");
      elements[newIndex].focus();
    }
  }, [focusableElements, game]);

  const handleGamepadButtonPress = useCallback((button) => {
    // Prevent any button press if we just returned from lobby
    const now = Date.now();
    if (now - lastViewChangeTime.current < 500) {
      return;
    }

    // X button acts as Enter/Click (unless on an input field where it opens keyboard)
    if (button === 'X') {
      const isOnInput = document.activeElement === playerNameInputRef.current ||
                       document.activeElement === roomCodeInputRef.current;

      if (isOnInput) {
        // On input fields, open virtual keyboard
        if (document.activeElement === playerNameInputRef.current) {
          setKeyboardInputType('name');
          setShowVirtualKeyboard(true);
          game?.sounds?.clickSound?.play();
        } else if (document.activeElement === roomCodeInputRef.current) {
          setKeyboardInputType('code');
          setShowVirtualKeyboard(true);
          game?.sounds?.clickSound?.play();
        }
      } else {
        // On buttons, act as selection
        const elements = document.querySelectorAll("[tabindex='0']");
        if (elements.length === 0) return;

        focusableElements.elements = elements;

        if (focusableElements.elements[focusableElements.index]) {
          focusableElements.elements[focusableElements.index].click();
          game?.sounds?.clickSound?.play();
        }
      }
    }
    // B button to go back
    else if (button === 'B') {
      game?.sounds?.clickSound?.play();
      navigate('/');
    }
  }, [game, focusableElements, navigate]);

  // Use the useGamepad hook for frame-rate independent input
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: isUIReady && currentView === 'main' && !showVirtualKeyboard,
    gamepadIndex: null
  });

  // Handle virtual keyboard submit
  const handleVirtualKeyboardSubmit = (value) => {
    if (keyboardInputType === 'name') {
      setPlayerNameInput(value);
      setHasUserEditedName(true);
      if (playerNameError) setPlayerNameError("");
    } else if (keyboardInputType === 'code') {
      setRoomCodeInput(value.toUpperCase());
      clearError();
    }
  };

  // Handle virtual keyboard close
  const handleVirtualKeyboardClose = () => {
    setShowVirtualKeyboard(false);
    setKeyboardInputType(null);
  };

  if (currentView === "lobby") {
    return (
      <RoomLobby
        roomData={roomData}
        onBack={handleBackToMain}
        onCopyCode={handleCopyRoomCode}
        playerName={currentPlayerName}
        game={game}
      />
    );
  }

  return (
    <div className={styles.pageContainer}>
      <CardOne
        customTag="section"
        attributes={{
          className: styles.mainCard,
          onKeyDown: (event) => {
            // ESC key always goes back
            if (event.key === 'Escape') {
              event.preventDefault();
              game?.sounds?.clickSound?.play();
              navigate('/');
              return;
            }

            // Allow arrow keys to navigate even from inputs
            const isInputFocused = event.target.tagName === 'INPUT';
            const isNavigationKey = event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter';

            // Handle navigation keys (work even when input is focused)
            if (!showVirtualKeyboard && isNavigationKey) {
              // Switch to keyboard mode
              isMouseModeRef.current = false;

              // Re-query elements to ensure they're current
              // Filter out disabled elements AND the back button
              const allElements = document.querySelectorAll("[tabindex='0']");
              const elements = Array.from(allElements).filter(el => {
                return !el.disabled && !el.className.includes('go-back-button');
              });
              if (elements.length > 0) {
                focusableElements.elements = elements;

                // Handle keyboard navigation with circular wrapping
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  // Find current focused element's index
                  const currentIndex = elements.indexOf(document.activeElement);

                  let newIndex;
                  if (currentIndex === -1) {
                    // Not found, start from beginning
                    newIndex = 0;
                  } else if (currentIndex >= elements.length - 1) {
                    // At end, wrap to first
                    newIndex = 0;
                  } else {
                    // Move to next
                    newIndex = currentIndex + 1;
                  }

                  focusableElements.index = newIndex;
                  if (elements[newIndex]) {
                    playSound(game, "navigation");
                    elements[newIndex].focus();
                  }
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  // Find current focused element's index
                  const currentIndex = elements.indexOf(document.activeElement);
                  let newIndex;
                  if (currentIndex === -1) {
                    // Not found, start from end
                    newIndex = elements.length - 1;
                  } else if (currentIndex <= 0) {
                    // At start, wrap to last
                    newIndex = elements.length - 1;
                  } else {
                    // Move to previous
                    newIndex = currentIndex - 1;
                  }
                  focusableElements.index = newIndex;
                  if (elements[newIndex]) {
                    playSound(game, "navigation");
                    elements[newIndex].focus();
                  }
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  if (elements[focusableElements.index]) {
                    elements[focusableElements.index].click();
                  }
                }
              }
            }
          },
          onMouseMove: (event) => {
            if (!showVirtualKeyboard && isMouseModeRef.current !== false) {
              // Only process if not in keyboard mode
              // Switch to mouse mode
              isMouseModeRef.current = true;

              // Re-query elements for mouse navigation
              // Filter out disabled elements
              const allElements = document.querySelectorAll("[tabindex='0']");
              const elements = Array.from(allElements).filter(el => !el.disabled);
              if (elements.length > 0) {
                focusableElements.elements = elements;
                // When hovering a new element, blur ALL focused elements (mouse mode)
                focusableElements?.elements?.forEach((element, index) => {
                  if (event.target === element && index !== focusableElements.index) {
                    // Blur all elements when in mouse mode
                    focusableElements.elements.forEach(el => {
                      if (el === document.activeElement) {
                        el.blur();
                      }
                    });
                    focusableElements.index = index;
                  }
                });
              }
            }
          },
          tabIndex: -1
        }}
      >
        <GoBackButton onClickHandler={() => navigate('/')} game={game} />

        <div className={styles.container}>
          <div className={styles.header}>
            <FontAwesomeIcon icon={faUsers} className={styles.headerIcon} />
            <h1 className={styles.title}>Online Multiplayer</h1>
          </div>

          {/* Fixed height container to prevent layout shift */}
          <div className={styles.connectionStatusContainer}>
            {(connectionError || !isConnected) && (
              <div
                className={`${styles.errorMessage} ${
                  styles[`errorMessage-theme-${themeNumber}`]
                }`}
              >
                {connectionError || "Connecting to server..."}
              </div>
            )}
          </div>

          {/* Player Name Input */}
          <div className={styles.playerNameSection}>
            <input
              ref={playerNameInputRef}
              type="text"
              value={playerNameInput}
              onChange={(e) => {
                setPlayerNameInput(e.target.value);
                setHasUserEditedName(true);
                if (playerNameError) setPlayerNameError("");
              }}
              placeholder="ENTER YOUR NAME"
              maxLength={20}
              className={`${styles.playerNameInput} ${
                styles[`playerNameInput-theme-${themeNumber}`]
              } ${playerNameError ? styles.playerNameInputError : ""}`}
              tabIndex="0"
            />
            <div className={styles.errorTextContainer}>
              {playerNameError && (
                <div className={styles.errorText}>
                  {playerNameError}
                </div>
              )}
            </div>
          </div>

          <div className={styles.actionsContainer}>
            {/* Create Room Section */}
            <div
              className={`${styles.actionCard} ${
                styles[`actionCard-theme-${themeNumber}`]
              }`}
            >
              <div className={styles.actionHeader}>
                <FontAwesomeIcon icon={faPlus} className={styles.actionIcon} />
                <h2 className={styles.actionTitle}>Create Room</h2>
              </div>
              <p className={styles.actionDescription}>
                Start a new game and invite friends with a room code
              </p>
              <button
                className={`${styles.actionButton} ${
                  styles[`actionButton-theme-${themeNumber}`]
                }`}
                onClick={handleCreateRoom}
                onMouseDown={(e) => e.preventDefault()}
                disabled={isLoading}
                tabIndex="0"
              >
                {isLoading ? (
                  <>
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className={styles.spinnerIcon}
                    />
                    Creating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} />
                    Create Room
                  </>
                )}
              </button>
            </div>

            {/* Join Room Section */}
            <div
              className={`${styles.actionCard} ${
                styles[`actionCard-theme-${themeNumber}`]
              }`}
            >
              <div className={styles.actionHeader}>
                <FontAwesomeIcon
                  icon={faSignInAlt}
                  className={styles.actionIcon}
                />
                <h2 className={styles.actionTitle}>Join Room</h2>
              </div>
              <p className={styles.actionDescription}>
                Enter a 6-character room code to join an existing game
              </p>

              <div className={styles.inputContainer}>
                <div className={styles.inputWrapper}>
                  <FontAwesomeIcon icon={faKey} className={styles.inputIcon} />
                  <input
                    ref={roomCodeInputRef}
                    type="text"
                    value={roomCodeInput}
                    onChange={handleRoomCodeInputChange}
                    onKeyDown={handleRoomCodeKeyDown}
                    placeholder="ENTER CODE"
                    className={`${styles.roomCodeInput} ${
                      styles[`roomCodeInput-theme-${themeNumber}`]
                    }`}
                    maxLength="6"
                    tabIndex="0"
                  />
                </div>
                <button
                  className={`${styles.actionButton} ${
                    styles[`actionButton-theme-${themeNumber}`]
                  }`}
                  onClick={handleJoinRoom}
                  onMouseDown={(e) => e.preventDefault()}
                  disabled={isLoading || roomCodeInput.length !== 6}
                  tabIndex="0"
                >
                  {isLoading ? (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className={styles.spinnerIcon}
                    />
                  ) : (
                    <FontAwesomeIcon icon={faSignInAlt} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {hasGamepad && (
            <div className={styles.helpText}>
              <p>• Press X button to open virtual keyboard when focused on input</p>
            </div>
          )}
        </div>
      </CardOne>

      {/* Virtual Keyboard Modal */}
      {showVirtualKeyboard && (
        <VirtualKeyboardModal
          initialValue={keyboardInputType === 'name' ? playerNameInput : roomCodeInput}
          onSubmit={handleVirtualKeyboardSubmit}
          onClose={handleVirtualKeyboardClose}
          theme={themeNumber}
          game={game}
          maxLength={keyboardInputType === 'name' ? 20 : 6}
          allowedPattern={keyboardInputType === 'code' ? /[A-Z0-9]/ : null}
        />
      )}
    </div>
  );
};
